// hooks/useCart.js
import { useState, useEffect, useCallback, useRef, useMemo } from 'react';
import { DecodePackCombinations } from '../../../utils/DecodePackComb';
import axios from 'axios';

const cartListeners = new Set();

const notifyCartUpdate = (cartData) => {
    cartListeners.forEach(listener => {
        try {
            listener(cartData);
        } catch (error) {
            console.error('Errore notifica listener:', error);
        }
    });
};

const CACHE_DURATION = 3000;

const initializePackagingForItems = (items) => {
    if (!items || items.length === 0) return items;
    
    return items.map(item => {
        if (item.packaging_combo && item.packaging_cost) {
            return {
                ...item,
                packaging: {
                    combination: item.packaging_combo,
                    description: item.packaging_description,
                    cost: item.packaging_cost,
                    packagingCost: item.packaging_cost,
                    details: item.packaging_details,
                    fromDB: true
                }
            };
        }
        
        if ([3, 6, 12].includes(item.quantita)) {
            const PACKAGING_COSTS = { 3: 1.50, 6: 2.50, 12: 3.50 };
            const cost = PACKAGING_COSTS[item.quantita] || 0;
            
            return {
                ...item,
                packaging: {
                    combination: [item.quantita],
                    description: `1 × confezione da ${item.quantita}`,
                    cost: cost,
                    packagingCost: cost,
                    totalPacks: 1,
                    counts: { [item.quantita]: 1 },
                    fromDB: false,
                    pending: true
                }
            };
        }
        
        if (item.quantita > 12) {
            const result = DecodePackCombinations(item.quantita);
            if (result.mostEfficient) {
                return {
                    ...item,
                    packaging: {
                        ...result.mostEfficient,
                        packagingCost: result.mostEfficient.packagingCost,
                        cost: result.mostEfficient.packagingCost,
                        fromDB: false,
                        pending: true
                    }
                };
            }
        }
        
        return item;
    });
};

const useCart = () => {
    const [cart, setCart] = useState({
        items: [],
        subtotal: 0,
        item_count: 0
    });
    const isInitializedRef = useRef(false);
    const [loading, setLoading] = useState(false);
    const [error, setError] = useState(null);
    const [initialized, setInitialized] = useState(false);
    const [lastFetch, setLastFetch] = useState(0);
    
    const abortControllerRef = useRef(null);
    const pendingRequestsRef = useRef(new Set());
    
    const [sessionId, setSessionId] = useState(() => {
        return localStorage.getItem('session_id') || 
            `guest_${Date.now()}_${Math.random().toString(36).substr(2, 9)}`;
    });

    const API_URL = process.env.REACT_APP_API_URL || '/api';

    const itemsLength = useMemo(() => cart.items.length, [cart.items]);

    const createApiClient = useCallback((withAuth = false) => {
        const instance = axios.create({
            baseURL: API_URL,
            withCredentials: true,
            timeout: 10000,
            headers: { 'Content-Type': 'application/json' }
        });

        instance.interceptors.request.use(
            (config) => {
                if (abortControllerRef.current) {
                    abortControllerRef.current.abort();
                }
                abortControllerRef.current = new AbortController();
                config.signal = abortControllerRef.current.signal;
                
                const currentSessionId = localStorage.getItem('session_id') || sessionId;
                if (currentSessionId) {
                    config.headers['X-Session-Id'] = currentSessionId;
                }
                
                if (withAuth) {
                    const token = localStorage.getItem('access_token');
                    if (token) {
                        config.headers.Authorization = `Bearer ${token}`;
                    }
                }
                
                return config;
            },
            (error) => Promise.reject(error)
        );

        return instance;
    }, [API_URL, sessionId]);

    const fetchCart = useCallback(async (forceRefresh = false) => {
        const now = Date.now();
        
        if (!forceRefresh && now - lastFetch < CACHE_DURATION && itemsLength > 0) {
            return cart;
        }
        
        const requestKey = 'fetchCart';
        if (pendingRequestsRef.current.has(requestKey)) {
            return cart;
        }
        
        setLoading(true);
        setError(null);
        pendingRequestsRef.current.add(requestKey);
        
        try {
            const client = createApiClient(true);
            const response = await client.get('/cart');
            
            if (response.data.success) {
                let cartData = response.data.cart;
                if (cartData.items?.length > 0) {
                    cartData.items = initializePackagingForItems(cartData.items);
                }
                setCart(cartData);
                setInitialized(true);
                setLastFetch(now);
                notifyCartUpdate(cartData);
                return cartData;
            }
            
            throw new Error('Errore nel recupero del carrello');
            
        } catch (err) {
            if (err.name === 'AbortError' || err.name === 'CanceledError') {
                return cart;
            }
            console.error('🔥 Errore fetch cart:', err);
            setError(err.message);
            const emptyCart = { items: [], subtotal: 0, item_count: 0 };
            setCart(emptyCart);
            notifyCartUpdate(emptyCart);
            return emptyCart;
        } finally {
            setLoading(false);
            pendingRequestsRef.current.delete(requestKey);
        }
    }, [createApiClient, cart, lastFetch, itemsLength]);


    // in useCart.js - addToCart
    const addToCart = useCallback(async (productData) => {

    // ✅ ESTRAI I DATI PACKAGING
    const packagingCost = productData.packaging_cost || 0;
    const packagingDescription = productData.packaging_description || '';
    const packagingCombo = productData.packaging_combo || [];

    /*
    * debug 09-04-26
    */
    console.log('🔍 FASE 2 - productData ricevuto:', {
        packaging_cost: productData.packaging_cost,
        packaging_description: productData.packaging_description,
        packaging_combo: productData.packaging_combo
    });
    

        const quantity = calculateQuantity(productData);
        
        const payload = {
            productId: productData.productId || productData.idProdotto,
            quantity,
            prezzoUnitario: productData.prezzoUnitario || null,
            vigneta: productData.vigneta || productData.img_cartart || null,
            img_cartart: productData.vigneta || productData.img_cartart || null,

             // ✅ INVIA I DATI PACKAGING AL BACKEND
            packaging_cost: packagingCost,
            packaging_description: packagingDescription,
            packaging_combo: packagingCombo
        };

        setLoading(true);
        setError(null);
        
        const previousCart = { ...cart };
        
        setCart(prev => ({
            ...prev,
            item_count: (prev.item_count || 0) + quantity
        }));
        notifyCartUpdate({ ...cart, item_count: cart.item_count + quantity });
        
        try {
            const client = createApiClient(true);
            /*
            * debug 09-04-26
            */
            console.log('🔍 FASE 3 - payload inviato al backend:', {
                packaging_cost: payload.packaging_cost,
                packaging_description: payload.packaging_description,
                packaging_combo: payload.packaging_combo
            });
            const response = await client.post('/cart/items', payload);

            if (response.data.cart) {
                setCart(response.data.cart);
                notifyCartUpdate(response.data.cart);
            } else {
                await fetchCart(true);
            }
            
            return {
                success: true,
                message: response.data.message,
                cart: response.data.cart || response.data,
                cartItem: response.data.cartItem
            };
            
        } catch (err) {
            setCart(previousCart);
            notifyCartUpdate(previousCart);
            
            const errorMessage = err.response?.data?.message || err.message || 'Errore nell\'aggiunta al carrello';
            setError(errorMessage);
            return { success: false, error: errorMessage };
        } finally {
            setLoading(false);
        }
    }, [createApiClient, fetchCart, cart]);

    const updatePackQuantities = useCallback(async (itemId, packQuantities, totalQuantity) => {
        setLoading(true);
        
        try {
            const PACKAGING_COSTS = { 3: 1.50, 6: 2.50, 12: 3.50 };
            let packagingCost = 0;
            Object.entries(packQuantities).forEach(([size, count]) => {
                packagingCost += (PACKAGING_COSTS[size] || 0) * count;
            });
            
            const currentItem = cart.items.find(item => item.id === itemId);
            const unitPrice = currentItem?.prezzo_unitario || 0;
            
            let subtotal = 0;
            Object.entries(packQuantities).forEach(([size, count]) => {
                subtotal += unitPrice * parseInt(size) * count;
            });
            
            const payload = { packQuantities, totalQuantity, packagingCost, subtotal };
            const originalItemData = currentItem ? { ...currentItem } : null;
            
            setCart(currentCart => {
                const updatedItems = currentCart.items.map(item => 
                    item.id === itemId 
                        ? { 
                            ...originalItemData,
                            quantita: totalQuantity,
                            prezzo_totale: subtotal,
                            packaging_combo: Object.values(packQuantities).flatMap((count, idx) => 
                                Array(count).fill([3, 6, 12][idx])
                            ).filter(Boolean),
                            packaging_cost: packagingCost,
                            packaging_description: Object.entries(packQuantities)
                                .filter(([_, count]) => count > 0)
                                .map(([size, count]) => `${count} × confezione da ${size}`)
                                .join(' + '),
                            packaging: {
                                ...(originalItemData?.packaging || {}),
                                packagingCost: packagingCost,
                                cost: packagingCost,
                                combination: Object.values(packQuantities).flatMap((count, idx) => 
                                    Array(count).fill([3, 6, 12][idx])
                                ).filter(Boolean),
                                description: Object.entries(packQuantities)
                                    .filter(([_, count]) => count > 0)
                                    .map(([size, count]) => `${count} × confezione da ${size}`)
                                    .join(' + ')
                            }
                        }
                        : item
                );
                
                const newSubtotal = updatedItems.reduce((sum, item) => sum + (item.prezzo_totale || 0), 0);
                const newCart = { ...currentCart, items: updatedItems, subtotal: newSubtotal };
                notifyCartUpdate(newCart);
                return newCart;
            });
            
            const client = createApiClient(true);
            await client.put(`/cart/items/${itemId}/packs`, payload);
            return { success: true };
            
        } catch (err) {
            console.error('🔥 Errore updatePackQuantities:', err);
            await fetchCart(true);
            return { success: false, error: err.message };
        } finally {
            setLoading(false);
        }
    }, [createApiClient, cart, fetchCart]);

    const getTotalPackagingCost = useCallback(() => {
        if (!cart?.items?.length) return 0;
        
        return cart.items.reduce((total, item) => {
            let cost = 0;
            if (item.packaging?.packagingCost) {
                cost = Number(item.packaging.packagingCost);
            } else if (item.packaging?.cost) {
                cost = Number(item.packaging.cost);
            } else if ([3, 6, 12].includes(item.quantita)) {
                const COSTS = { 3: 1.50, 6: 2.50, 12: 3.50 };
                cost = COSTS[item.quantita] || 0;
            }
            return total + cost;
        }, 0);
    }, [cart.items]);

    const removeCartItem = useCallback(async (itemId) => {
        setLoading(true);
        
        const previousCart = { ...cart };
        const itemToRemove = cart.items.find(item => item.id === itemId);
        const quantityToRemove = itemToRemove?.quantita || 1;
        
        setCart(prev => ({
            ...prev,
            item_count: Math.max(0, (prev.item_count || 0) - quantityToRemove)
        }));
        
        try {
            const client = createApiClient(true);
            await client.delete(`/cart/items/${itemId}`);
            await fetchCart(true);
            return { success: true, message: 'Articolo rimosso' };
        } catch (err) {
            console.error('🔥 removeCartItem error:', err);
            setCart(previousCart);
            notifyCartUpdate(previousCart);
            return { success: false, error: err.response?.data?.message || err.message };
        } finally {
            setLoading(false);
        }
    }, [createApiClient, fetchCart, cart]);

    const clearCart = useCallback(async () => {
        console.log('🗑️ clearCart chiamato');
        setLoading(true);
        
        const previousCart = { ...cart };
        const emptyCart = { items: [], subtotal: 0, item_count: 0 };
        
        setCart(emptyCart);
        notifyCartUpdate(emptyCart);
        
        try {
            const client = createApiClient(true);
            console.log('📡 Chiamata DELETE a /cart/clear');
            const response = await client.delete('/cart/clear');
            console.log('✅ Risposta clearCart:', response.data);
            notifyCartUpdate(emptyCart);
            return { success: true, message: 'Carrello svuotato' };
        } catch (err) {
            console.error('❌ clearCart error:', err);
            console.error('❌ Dettaglio errore:', err.response?.data);
            setCart(previousCart);
            notifyCartUpdate(previousCart);
            return { success: false, error: err.response?.data?.message || err.message };
        } finally {
            setLoading(false);
        }
    }, [createApiClient, cart]);

    useEffect(() => {
        const handleUserLogin = (event) => {
            const newSessionId = `user_${event.detail.userId}`;
            setSessionId(newSessionId);
            localStorage.setItem('session_id', newSessionId);
            fetchCart(true);
        };

        const handleUserLogout = () => {
            const guestSessionId = `guest_${Date.now()}_${Math.random().toString(36).substr(2, 9)}`;
            setSessionId(guestSessionId);
            localStorage.setItem('session_id', guestSessionId);
            
            const emptyCart = { items: [], subtotal: 0, item_count: 0 };
            setCart(emptyCart);
            notifyCartUpdate(emptyCart);
            
            setTimeout(() => {
                fetchCart(true);
            }, 100);
        };

        window.addEventListener('userLoggedIn', handleUserLogin);
        window.addEventListener('userLoggedOut', handleUserLogout);

        return () => {
            window.removeEventListener('userLoggedIn', handleUserLogin);
            window.removeEventListener('userLoggedOut', handleUserLogout);
            if (abortControllerRef.current) {
                abortControllerRef.current.abort();
            }
        };
    }, [fetchCart]);

    useEffect(() => {
        if (!isInitializedRef.current && !loading) {
            isInitializedRef.current = true;
            fetchCart();
        }
    }, [loading, fetchCart]);

    const subscribe = useCallback((listener) => {
        cartListeners.add(listener);
        listener(cart);
        return () => cartListeners.delete(listener);
    }, [cart]);

    const refreshCart = useCallback(() => {
        return fetchCart(true);
    }, [fetchCart]);

    return {
        cart,
        loading,
        error,
        initialized,
        sessionId,
        fetchCart,
        addToCart,
        removeCartItem,
        clearCart,
        updatePackQuantities,
        getTotalPackagingCost,
        refreshCart,
        subscribe
    };
};

const calculateQuantity = (productData) => {
    if (productData.quantity) return Number(productData.quantity);
    if (productData.quantita) return Number(productData.quantita);
    
    if (productData.confezione) {
        if (typeof productData.confezione === 'number') {
            return productData.confezione;
        }
        if (typeof productData.confezione === 'object') {
            if (productData.confezione.quantita) return Number(productData.confezione.quantita);
            if (productData.confezione.tipo === 'box3') return 3;
            if (productData.confezione.tipo === 'box6') return 6;
            if (productData.confezione.tipo === 'box12') return 12;
        }
    }
    
    return 1;
};

export default useCart;
